import { ApolloError } from '@apollo/client';

type ToastFunction = (
  message: string,
  variant: 'default' | 'error' | 'success' | 'warning' | 'info'
) => void;

interface ErrorPatterns {
  pattern: string;
  friendlyMessage: string | ((match: RegExpMatchArray, originalInput: string) => string);
}

// Common error patterns that can be reused across the application
const commonErrorPatterns: ErrorPatterns[] = [
  {
    pattern: 'duplicate key value.*uq_supplier_name_company',
    friendlyMessage: (match, input) => {
      // Extract the name from the error message if possible
      const nameMatch = input.match(/Key \(name, company_id\)=\((.*?),/);
      const name = nameMatch ? nameMatch[1] : '';
      return `A supplier with the name "${name}" already exists`;
    },
  },
  {
    pattern: 'duplicate key value.*uq_location_name_company',
    friendlyMessage: (match, input) => {
      // Extract the name from the error message if possible
      const nameMatch = input.match(/Key \(name, company_id\)=\((.*?),/);
      const name = nameMatch ? nameMatch[1] : '';
      return `A location with the name "${name}" already exists`;
    },
  },
];

/**
 * Extracts a user-friendly error message from GraphQL errors
 * @param error - The error object from Apollo Client
 * @param customPatterns - Optional additional patterns specific to the calling component
 * @returns A user-friendly error message
 */
export const getErrorMessage = (error: unknown, customPatterns: ErrorPatterns[] = []): string => {
  // Combine custom patterns with common patterns
  const allPatterns = [...customPatterns, ...commonErrorPatterns];

  let errorMessage = 'An unexpected error occurred';

  if (error instanceof ApolloError) {
    // Get the first GraphQL error message or network error
    errorMessage = error.graphQLErrors?.[0]?.message || error.networkError?.message || errorMessage;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    errorMessage = error.message;
  }

  // Use array methods instead of for...of loop
  const matchingPattern = allPatterns.find(({ pattern }) => {
    const regex = new RegExp(pattern);
    return regex.test(errorMessage);
  });

  if (matchingPattern) {
    const regex = new RegExp(matchingPattern.pattern);
    const match = errorMessage.match(regex);

    // Make sure match is not null before proceeding
    if (match) {
      if (typeof matchingPattern.friendlyMessage === 'function') {
        return matchingPattern.friendlyMessage(match, errorMessage);
      }
      return matchingPattern.friendlyMessage;
    }
  }

  // If no patterns match, you might want to return a default message or the original error
  return errorMessage;
};

/**
 * A utility function to handle GraphQL operation responses
 * @param response - The response from a GraphQL operation
 * @param showToast - The toast function to display messages
 * @param operationName - The name of the operation for logging
 * @param customPatterns - Optional additional error patterns
 * @returns true if the operation was successful, false otherwise
 */
export const handleGraphQLResponse = <T>(
  response: { data?: T; errors?: readonly any[] },
  showToast: ToastFunction,
  operationName: string,
  customPatterns: ErrorPatterns[] = []
): boolean => {
  // Check if there are GraphQL errors
  if (response.errors || !response.data) {
    const errorObj = response.errors?.[0];
    const errorMessage = errorObj
      ? getErrorMessage(errorObj, customPatterns)
      : `Failed to ${operationName}`;

    showToast(errorMessage, 'error');
    console.error(`GraphQL Error in ${operationName}:`, response.errors);
    return false;
  }

  return true;
};

/**
 * A utility function to handle errors in catch blocks
 * @param error - The caught error
 * @param showToast - The toast function to display messages
 * @param operationName - The name of the operation for logging
 * @param customPatterns - Optional additional error patterns
 */
export const handleCatchError = (
  error: unknown,
  showToast: ToastFunction,
  operationName: string,
  customPatterns: ErrorPatterns[] = []
): void => {
  const errorMessage = getErrorMessage(error, customPatterns);
  showToast(errorMessage, 'error');
  console.error(`Error in ${operationName}:`, error);
};
